import React, { useState, useEffect, useRef } from 'react';
import { FileText, Check, X, Search, ExternalLink, ChevronDown, Clock, AlertTriangle, Eye, FileEdit, Copy, Download } from 'lucide-react';
import api from '../services/api';

const confidenceColor = (score) => {
  if (score >= 0.90) return 'bg-green-900/50 text-green-300 border-green-700';
  if (score >= 0.70) return 'bg-yellow-900/50 text-yellow-300 border-yellow-700';
  return 'bg-red-900/50 text-red-300 border-red-700';
};

const confidenceLabel = (score) => {
  if (score >= 0.90) return 'High';
  if (score >= 0.70) return 'Medium';
  return 'Low';
};

const deadlineColor = (dateStr) => {
  if (!dateStr) return 'bg-gray-700 text-gray-400';
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (days <= 3) return 'bg-red-900/60 text-red-300';
  if (days <= 7) return 'bg-orange-900/60 text-orange-300';
  if (days <= 14) return 'bg-yellow-900/60 text-yellow-300';
  return 'bg-gray-700 text-gray-400';
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `${days}d`;
};

const EserviceFilingsWidget = () => {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftModal, setDraftModal] = useState(null);

  const fetchFilings = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/eservice/filings/pending');
      setFilings(resp.data || []);
    } catch (err) {
      console.error('Failed to load eservice filings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilings();
    const interval = setInterval(fetchFilings, 120000);
    return () => clearInterval(interval);
  }, []);

  const handleConfirm = async (filingId, caseId) => {
    try {
      await api.post(`/eservice/filings/${filingId}/confirm`, {
        case_id: caseId,
        alias_type: 'case_style',
      });
      setFilings((prev) => prev.filter((f) => f.id !== filingId));
    } catch (err) {
      console.error('Confirm failed:', err);
    }
  };

  const handleDismiss = async (filingId) => {
    try {
      await api.post(`/eservice/filings/${filingId}/dismiss`);
      setFilings((prev) => prev.filter((f) => f.id !== filingId));
    } catch (err) {
      console.error('Dismiss failed:', err);
    }
  };

  if (loading && filings.length === 0) {
    return (
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <div className="text-gray-500 text-sm animate-pulse">Loading eService filings...</div>
      </div>
    );
  }

  if (filings.length === 0) return null;

  // Summary stats
  const draftsReady = filings.filter(f => f.draft_response_text).length;
  const deadlineThisWeek = filings.filter(f => {
    if (!f.nearest_deadline_date) return false;
    const days = Math.ceil((new Date(f.nearest_deadline_date) - new Date()) / 86400000);
    return days >= 0 && days <= 7;
  }).length;

  return (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-amber-700/50 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wide">
            eService Filing Queue
          </h3>
        </div>
        <span className="text-xs bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded-full">
          {filings.length}
        </span>
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-4 text-xs text-gray-400 mb-3 pb-2 border-b border-gray-700/50">
        <span>{filings.length} pending</span>
        {draftsReady > 0 && (
          <span className="text-blue-400">{draftsReady} draft{draftsReady > 1 ? 's' : ''} ready</span>
        )}
        {deadlineThisWeek > 0 && (
          <span className="text-red-400">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            {deadlineThisWeek} deadline{deadlineThisWeek > 1 ? 's' : ''} this week
          </span>
        )}
      </div>

      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {filings.map((filing) => (
          <FilingCard
            key={filing.id}
            filing={filing}
            onConfirm={handleConfirm}
            onDismiss={handleDismiss}
            onViewDraft={() => setDraftModal(filing)}
          />
        ))}
      </div>

      {/* Draft modal */}
      {draftModal && (
        <DraftModal filing={draftModal} onClose={() => setDraftModal(null)} />
      )}
    </div>
  );
};

const FilingCard = ({ filing, onConfirm, onDismiss, onViewDraft }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestedCase, setSuggestedCase] = useState(null);
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  // Load suggested case details
  useEffect(() => {
    if (filing.suggested_case_id && !suggestedCase) {
      api.get('/eservice/matters/search', { params: { q: filing.suggested_case_id } })
        .catch(() => {});
      // Use the search endpoint to find by ID — or just show the ID
      setSuggestedCase({ id: filing.suggested_case_id });
    }
  }, [filing.suggested_case_id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setSearchQuery(value);
    setSelectedCase(null);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        setSearching(true);
        const resp = await api.get('/eservice/matters/search', { params: { q: value } });
        setSearchResults(resp.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Matter search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const selectCase = (c) => {
    setSelectedCase(c);
    setSearchQuery(c.case_name);
    setShowDropdown(false);
  };

  const analysis = filing.analysis_json;

  return (
    <div className="bg-gray-900/60 rounded-lg border border-gray-700 hover:border-gray-600 transition">
      {/* Header row */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-200 truncate">
              {filing.case_style || 'Unknown Case Style'}
            </span>
            {/* Confidence badge */}
            {filing.match_confidence != null && filing.match_confidence > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${confidenceColor(filing.match_confidence)}`}>
                {confidenceLabel(filing.match_confidence)} {Math.round(filing.match_confidence * 100)}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
            {filing.court && <span>{filing.court}</span>}
            {filing.filing_date && <span>{filing.filing_date}</span>}
            {filing.filing_type && <span className="text-gray-400">{filing.filing_type}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-2">
          {/* Deadline pill */}
          {filing.nearest_deadline_date && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${deadlineColor(filing.nearest_deadline_date)}`}>
              <Clock className="w-3 h-3" />
              {daysUntil(filing.nearest_deadline_date)}
            </span>
          )}
          {/* Draft ready indicator */}
          {filing.draft_response_text && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/50 text-blue-300 border border-blue-700">
              Draft
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-700/50 pt-2 space-y-2">
          {filing.filer && (
            <div className="text-xs text-gray-400">
              <span className="text-gray-600">Filer:</span> {filing.filer}
            </div>
          )}
          {filing.filing_number && (
            <div className="text-xs text-gray-400">
              <span className="text-gray-600">Filing #:</span> {filing.filing_number}
            </div>
          )}
          {filing.deadline_count > 0 && (
            <div className="text-xs text-gray-400">
              <span className="text-gray-600">Deadlines:</span> {filing.deadline_count}
              {filing.nearest_deadline_date && (
                <span className="ml-1">| Nearest: {new Date(filing.nearest_deadline_date).toLocaleDateString()}</span>
              )}
            </div>
          )}
          {filing.document_links?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {filing.document_links.map((link, i) => (
                <a
                  key={i}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3" />
                  Doc {i + 1}
                </a>
              ))}
            </div>
          )}

          {/* Analysis panel */}
          {analysis && (
            <div className="mt-2">
              <button
                onClick={(e) => { e.stopPropagation(); setShowAnalysis(!showAnalysis); }}
                className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
              >
                <Eye className="w-3 h-3" />
                {showAnalysis ? 'Hide' : 'View'} Analysis
              </button>
              {showAnalysis && (
                <div className="mt-2 bg-gray-800/80 rounded p-3 border border-gray-700 space-y-2 text-xs">
                  <div>
                    <span className="text-gray-500">Type:</span>{' '}
                    <span className="text-gray-300">{analysis.document_type}</span>
                  </div>
                  {analysis.summary && (
                    <div>
                      <span className="text-gray-500">Summary:</span>{' '}
                      <span className="text-gray-300">{analysis.summary}</span>
                    </div>
                  )}
                  {analysis.key_facts?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Key Facts:</span>
                      <ul className="list-disc list-inside text-gray-300 mt-1">
                        {analysis.key_facts.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  )}
                  {analysis.action_items?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Action Items:</span>
                      <div className="mt-1 space-y-1">
                        {analysis.action_items.map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              item.priority === 'urgent' ? 'bg-red-400' :
                              item.priority === 'normal' ? 'bg-yellow-400' : 'bg-gray-400'
                            }`} />
                            <span className="text-gray-300">{item.action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysis.flags?.length > 0 && (
                    <div className="bg-red-900/20 rounded p-2 border border-red-800/50">
                      <span className="text-red-400 font-medium">Flags:</span>
                      <ul className="list-disc list-inside text-red-300 mt-1">
                        {analysis.flags.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Suggested match (one-click confirm for 0.70-0.89 confidence) */}
          {filing.suggested_case_id && !selectedCase && (
            <div className="bg-yellow-900/20 rounded p-2 border border-yellow-800/50 flex items-center justify-between">
              <span className="text-xs text-yellow-300">
                Suggested match: {filing.suggested_case_id}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm(filing.id, filing.suggested_case_id);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-700 hover:bg-yellow-600 text-white"
              >
                <Check className="w-3 h-3" />
                Confirm
              </button>
            </div>
          )}

          {/* Matter typeahead */}
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search matters to match..."
                className="w-full bg-gray-800 border border-gray-600 rounded pl-8 pr-3 py-1.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-amber-500"
                onClick={(e) => e.stopPropagation()}
              />
              {searching && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                {searchResults.map((c) => (
                  <div
                    key={c.id}
                    className="px-3 py-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700/50 last:border-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectCase(c);
                    }}
                  >
                    <div className="text-sm text-gray-200">{c.case_name}</div>
                    <div className="text-xs text-gray-500">
                      #{c.display_number || c.case_number}
                      {c.client_name && ` · ${c.client_name}`}
                      {c.status && ` · ${c.status}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (selectedCase) onConfirm(filing.id, selectedCase.id);
              }}
              disabled={!selectedCase}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition ${
                selectedCase
                  ? 'bg-green-700 hover:bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Check className="w-3.5 h-3.5" />
              Confirm Match
            </button>
            {filing.draft_response_text && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDraft();
                }}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-blue-800 hover:bg-blue-700 text-blue-200 transition"
              >
                <FileEdit className="w-3.5 h-3.5" />
                View Draft
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(filing.id);
              }}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-700 hover:bg-red-900/50 text-gray-400 hover:text-red-300 transition"
            >
              <X className="w-3.5 h-3.5" />
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DraftModal = ({ filing, onClose }) => {
  const [editableText, setEditableText] = useState(filing.draft_response_text || '');
  const notes = (() => {
    try { return JSON.parse(filing.draft_response_notes || '{}'); }
    catch { return {}; }
  })();

  const handleCopy = () => {
    navigator.clipboard.writeText(editableText);
  };

  const handleDownload = () => {
    const blob = new Blob([editableText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response_${filing.case_style?.replace(/\s+/g, '_') || 'draft'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-gray-200">Draft Response</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {notes.recommended_type || filing.filing_type} — {filing.case_style}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCopy} className="p-2 hover:bg-gray-800 rounded text-gray-400 hover:text-gray-200" title="Copy">
              <Copy className="w-4 h-4" />
            </button>
            <button onClick={handleDownload} className="p-2 hover:bg-gray-800 rounded text-gray-400 hover:text-gray-200" title="Download">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded text-gray-400 hover:text-gray-200">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notes bar */}
        {(notes.strategic_notes || notes.flagged_issues?.length > 0) && (
          <div className="px-4 py-2 bg-gray-800/50 border-b border-gray-700 text-xs">
            {notes.strategic_notes && (
              <p className="text-gray-400 mb-1"><span className="text-gray-500">Strategy:</span> {notes.strategic_notes}</p>
            )}
            {notes.flagged_issues?.length > 0 && (
              <p className="text-red-400">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                {notes.flagged_issues.join(' · ')}
              </p>
            )}
          </div>
        )}

        {/* Editable draft */}
        <textarea
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          className="flex-1 p-4 bg-gray-950 text-gray-200 text-sm font-mono resize-none focus:outline-none overflow-y-auto"
          spellCheck={false}
        />
      </div>
    </div>
  );
};

export default EserviceFilingsWidget;
